import {
  Box,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Toolbar } from "common";
import useDictionaries from "features/dictionaries/useDictionaries";
import { ProgramItem } from "features/programs/programApi";
import moment from "moment";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { KitchenItem } from "./kitchenApi";
import OrderItem from "./OrderItem";

type KitchenStatus = "finished" | "running" | "next" | "upcoming";

const summaryColors = {
  option: "lightblue",
  variant: "lightgreen",
};

function getKitchenStatus(status: KitchenStatus) {
  switch (status) {
    case "finished":
      return { label: "lezajlott", color: "text.secondary" };
    case "running":
      return { label: "folyamatban", color: "success.main" };
    case "next":
      return { label: "következő", color: "warning.main" };
    case "upcoming":
      return { label: "", color: "text.primary" };
  }
}

function getItemStyles(status: KitchenStatus, theme: any) {
  if (status === "finished") {
    return {
      opacity: 0.6,
      backgroundColor: theme.palette.action.hover,
    };
  }
  return {};
}

function KitchenListItem({
  item,
  status,
}: {
  item: KitchenItem;
  status: KitchenStatus;
}) {
  const [open, setOpen] = useState(false);
  const { resolveOption } = useDictionaries();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const renderPrograms = useCallback(() => {
    return (
      <>
        {item.program_ids.map(program_id => {
          const program = resolveOption<ProgramItem>("programs", program_id);
          return (
            <Toolbar key={program_id} sx={{ gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "5px",
                  backgroundColor: program?.color,
                }}
              />
              <Typography>{program?.name}</Typography>
            </Toolbar>
          );
        })}
      </>
    );
  }, [item.program_ids]);

  const summary = item.summary;
  summary.sort((a, b) => b.qty - a.qty);

  const kitchenStatus = getKitchenStatus(status);
  const itemStyles = getItemStyles(status, theme);

  return (
    <ListItemButton
      component={Link}
      to={`${encodeURIComponent(item.start_time)}/${item.boat_id}`}
      state={{
        item,
      }}
      divider
      sx={itemStyles}
    >
      <ListItemText
        disableTypography
        primary={
          <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 500,
                color: kitchenStatus.color,
              }}
            >
              {`${moment(item.start_time).format("L LT")} - ${item.boat_name}`}
              {kitchenStatus.label && (
                <Typography
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: "0.8rem",
                    color: kitchenStatus.color,
                    fontWeight: 400,
                  }}
                >
                  ({kitchenStatus.label})
                </Typography>
              )}
            </Typography>
            {renderPrograms()}
          </Toolbar>
        }
        secondary={
          <Box
            display="flex"
            flexDirection="row"
            gap={1}
            mt={1}
            flexWrap="wrap"
          >
            {summary.map((summary, index) => (
              <OrderItem
                key={index}
                name={summary.name}
                qty={summary.qty}
                type={summary.type}
              />
            ))}
          </Box>
        }
      />
    </ListItemButton>
  );
}

export default KitchenListItem;
